import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { billsAPI } from '../utils/api';
import { useSettings } from '../hooks/useSettings';
import { DocHeader, DocFooter } from '../components/DocHeader';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

export default function BillDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideHeaderFooter, setHideHeaderFooter] = useState(false);
  const printRef = useRef();

  const cur = settings.currency || 'BDT';
  const primary = settings.primary_color || '#2563eb';

  useEffect(() => {
    billsAPI.getOne(id)
      .then(res => setBill(res.data))
      .catch(() => toast.error('Bill not found'))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: bill ? `Bill-${bill.bill_number}` : 'Bill',
    onAfterPrint: () => setHideHeaderFooter(false),
    pageStyle: `
      @page { size: A4; margin: 0; }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        body { margin: 0; padding: 0; }
      }
    `,
  });

  const handleDelete = async () => {
    if (!window.confirm(`Delete bill ${bill.bill_number}?`)) return;
    try {
      await billsAPI.delete(id);
      toast.success('Bill deleted');
      navigate('/bills');
    } catch { toast.error('Delete failed'); }
  };

  const fmtDate = d => d ? format(new Date(d), 'dd.MM.yyyy') : '-';

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>;
  if (!bill) return <div style={{ padding: 40 }}>Bill not found. <Link to="/bills">Go back</Link></div>;

  const emptyRows = Math.max(0, 6 - (bill.items?.length || 0));

  return (
    <div className="doc-wrapper">
      <Link to="/bills" className="back-link">← Back to Bills</Link>

      <div className="page-actions">
        <span className={`badge badge-${bill.status}`} style={{ fontSize: 13, padding: '5px 14px' }}>
          {bill.status?.toUpperCase()}
        </span>
        <button className="btn btn-outline" onClick={handlePrint}>🖨 Print / PDF</button>
        <button className="btn btn-outline" onClick={() => { setHideHeaderFooter(true); setTimeout(handlePrint, 100); }}>🖨 No Header/Footer</button>
        <Link to={`/bills/${id}/edit`} className="btn btn-outline">✏️ Edit</Link>
        <Link
          to="/receipts/new" className="btn btn-success"
          state={{ billId: id, billNumber: bill.bill_number, clientName: bill.client_name, clientAddress: bill.client_address }}
        >
          💳 Record Payment
        </Link>
        <button className="btn btn-danger" onClick={handleDelete}>🗑 Delete</button>
      </div>

      {/* Printable Document */}
      <div ref={printRef}>
        <div style={{
          background: '#fff',
          width: '100%',
          minHeight: '297mm',
          fontFamily: "'DM Sans', sans-serif",
          display: 'flex',
          flexDirection: 'column',
        }}>

          {/* HEADER */}
          {hideHeaderFooter ? (
            <div style={{ height: 120, width: '100%' }} />
          ) : (
            <DocHeader docType="BILL" />
          )}

          {/* BILL TO + META */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, padding: '20px 48px' }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.08em', borderBottom: '1.5px solid #e2e8f0', paddingBottom: 4, marginBottom: 8 }}>
                Bill To
              </div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{bill.client_name}</div>
              {bill.client_address && <div style={{ color: '#64748b', marginTop: 2, fontSize: 14 }}>{bill.client_address}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Date:</span>
                <span style={{ fontWeight: 600, color: primary }}>{fmtDate(bill.bill_date)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Bill No.</span>
                <span style={{ fontWeight: 700 }}>{bill.bill_number}</span>
              </div>
              {bill.due_date && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginBottom: 4, fontSize: 13 }}>
                  <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Due:</span>
                  <span>{fmtDate(bill.due_date)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 6, fontSize: 13, alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em' }}>Status:</span>
                <span className={`badge badge-${bill.status}`}>{bill.status}</span>
              </div>
            </div>
          </div>

          {/* ITEMS TABLE */}
          <div style={{ padding: '0 48px 20px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <thead style={{ backgroundColor: primary }}>
                <tr style={{ backgroundColor: primary }}>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', width: 60, textAlign: 'center' }}>SL.</th>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', textAlign: 'left' }}>Description</th>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', width: 80, textAlign: 'center' }}>QTY</th>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', width: 120, textAlign: 'right' }}>Unit Price</th>
                  <th style={{ padding: '12px 16px', backgroundColor: primary, color: '#fff', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '.05em', width: 130, textAlign: 'right' }}>Price ({cur})</th>
                </tr>
              </thead>
              <tbody>
                {bill.items?.map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', background: i % 2 === 1 ? '#f8fafc' : '#fff' }}>
                    <td style={{ padding: '13px 16px', textAlign: 'center', color: '#64748b', fontSize: 14 }}>{item.sl || i + 1}</td>
                    <td style={{ padding: '13px 16px', fontSize: 14 }}>{item.description}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'center', fontSize: 14 }}>{parseFloat(item.quantity)}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontSize: 14 }}>{parseFloat(item.unit_price).toLocaleString()}</td>
                    <td style={{ padding: '13px 16px', textAlign: 'right', fontWeight: 700, fontSize: 14 }}>{parseFloat(item.total).toLocaleString()}</td>
                  </tr>
                ))}
                {Array.from({ length: emptyRows }).map((_, i) => (
                  <tr key={`e${i}`} style={{ borderBottom: '1px solid #e2e8f0', background: ((bill.items?.length || 0) + i) % 2 === 1 ? '#f8fafc' : '#fff' }}>
                    <td style={{ padding: '13px 16px' }}>&nbsp;</td>
                    <td style={{ padding: '13px 16px' }}></td>
                    <td style={{ padding: '13px 16px' }}></td>
                    <td style={{ padding: '13px 16px' }}></td>
                    <td style={{ padding: '13px 16px' }}></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* TOTAL ROW */}
          <div style={{ padding: '0 48px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e2e8f0', marginTop: 4 }}>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Total</div>
            <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 800, fontSize: 18 }}>
              {cur} {parseFloat(bill.total).toLocaleString()}
            </div>
          </div>

          {/* DISCOUNT / TAX breakdown if present */}
          {(parseFloat(bill.discount) > 0 || parseFloat(bill.tax) > 0) && (
            <div style={{ padding: '0 48px 20px', display: 'flex', justifyContent: 'flex-end' }}>
              <table style={{ minWidth: 260, fontSize: 13 }}>
                {parseFloat(bill.discount) > 0 && (
                  <tr>
                    <td style={{ padding: '4px 12px', color: '#64748b' }}>Subtotal</td>
                    <td style={{ padding: '4px 12px', textAlign: 'right' }}>{cur} {parseFloat(bill.subtotal).toLocaleString()}</td>
                  </tr>
                )}
                {parseFloat(bill.discount) > 0 && (
                  <tr>
                    <td style={{ padding: '4px 12px', color: '#64748b' }}>Discount</td>
                    <td style={{ padding: '4px 12px', textAlign: 'right', color: '#ef4444' }}>- {cur} {parseFloat(bill.discount).toLocaleString()}</td>
                  </tr>
                )}
                {parseFloat(bill.tax) > 0 && (
                  <tr>
                    <td style={{ padding: '4px 12px', color: '#64748b' }}>Tax / VAT</td>
                    <td style={{ padding: '4px 12px', textAlign: 'right' }}>{cur} {parseFloat(bill.tax).toLocaleString()}</td>
                  </tr>
                )}
              </table>
            </div>
          )}

          {/* NOTES */}
          {bill.notes && (
            <div style={{ padding: '0 48px 20px' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#64748b', marginBottom: 4 }}>Notes</div>
              <div style={{ fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>{bill.notes}</div>
            </div>
          )}

          {/* SPACER — pushes footer to bottom */}
          <div style={{ flex: 1 }} />

          {/* FOOTER */}
          {hideHeaderFooter ? (
            <div style={{ height: 80, width: '100%' }} />
          ) : (
            <DocFooter />
          )}

        </div>
      </div>
    </div>
  );
}